import { formatDistanceToNow, format, isThisYear, differenceInHours, type Locale } from "date-fns";

// Utility functions for date handling

/**
 * Checks if a date falls on a weekend (Saturday or Sunday)
 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6; // 0 is Sunday, 6 is Saturday
}

/**
 * Simple list of banking holidays for the US
 * In a real app, these would be dynamically fetched from an API
 * or configured based on the user's location
 */
export const bankingHolidays2025: string[] = [
  "2025-01-01", // New Year's Day
  "2025-01-20", // Martin Luther King Jr. Day
  "2025-02-17", // Presidents' Day
  "2025-05-26", // Memorial Day
  "2025-07-04", // Independence Day
  "2025-09-01", // Labor Day
  "2025-10-13", // Columbus Day
  "2025-11-11", // Veterans Day
  "2025-11-27", // Thanksgiving Day
  "2025-12-25", // Christmas Day
];

/**
 * Checks if a date is a banking holiday (weekend or specific holiday)
 */
export function isBankingHoliday(date: Date): boolean {
  // A payable with no due date yet (a draft in Needs Input) has no day to be a holiday.
  if (Number.isNaN(date.getTime())) return false;

  // Check if it's a weekend
  if (isWeekend(date)) return true;
  
  
  // Format date as YYYY-MM-DD for comparison with our holiday list
  const formattedDate = date.toISOString().split('T')[0];
  
  // Check if it's in our list of banking holidays
  return bankingHolidays2025.includes(formattedDate);
}

/**
 * Find the closest business day before or on the specified date
 * Returns the same date if it's already a business day
 */
export function getClosestBusinessDayBefore(date: Date): Date {
  const result = new Date(date);
  
  // Keep going back days until we find a business day
  while (isBankingHoliday(result)) {
    result.setDate(result.getDate() - 1);
  }
  
  return result;
}

/**
 * Check if a date has been adjusted for banking holidays
 */
export function isAdjustedForHoliday(originalDate: Date, adjustedDate: Date): boolean {
  return originalDate.getTime() !== adjustedDate.getTime();
}

/**
 * Format a date as YYYY-MM-DD
 */
export function formatDateToYYYYMMDD(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Formats a date string as relative ("2 hours ago") if < 24h,
 * compact date ("14 Apr") if same year, or with year ("14 Apr 2025") otherwise, in the words of `locale`.
 */
export function formatRelativeDate(dateString: string, locale: Locale): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "";
  }
  if (differenceInHours(new Date(), date) < 24) {
    return formatDistanceToNow(date, { addSuffix: true, locale });
  }
  if (isThisYear(date)) {
    return format(date, "d MMM", { locale });
  }
  return format(date, "d MMM yyyy", { locale });
}
